import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useGetAllRequestsQuery } from 'services/user-service';
import {
  Box,
  Table,
  TableSortLabel,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useParams, useNavigate } from 'react-router-dom';
import { visuallyHidden } from '@mui/utils';
import GlobalStyles from '@mui/material/GlobalStyles';
import LoaderCircle from 'components/LoaderCircle';
import MonitorChart from './MonitorChart';
import Search from '../../layout/MainLayout/Header/HeaderContent/Search';
import { useSelector } from 'react-redux';

function createData(email, name, responseTime, requestType, leadsCount, usedCredits, status, createdAt, updatedAt, _id, userId) {
  return {
    email,
    name,
    responseTime,
    status,
    createdAt,
    updatedAt,
    leadsCount,
    usedCredits,
    _id,
    requestType,
    userId
  };
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getFormaDate(dateTimeString) {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const date = new Date(dateTimeString);
  const formattedDate = `${monthNames[String(date.getMonth())]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`;
  return formattedDate;
}

function getComparator(order, orderBy) {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'email',
    align: 'center',
    disablePadding: false,
    label: 'EMAIL'
  },
  {
    id: 'name',
    align: 'center',
    disablePadding: false,
    label: 'USER NAME'
  },
  {
    id: 'responseTime',
    align: 'center',
    disablePadding: false,
    tooltip: true,
    tooltipInfo: 'Total response time in seconds for APIs.',
    label: 'RESPONSE TIME (sec)'
  },
  {
    id: 'leadsCount',
    align: 'center',
    disablePadding: true,
    tooltip: true,
    tooltipInfo: 'Number of requested leads count.',
    label: 'COUNT'
  },
  {
    id: 'usedCredits',
    align: 'center',
    disablePadding: true,
    tooltip: true,
    tooltipInfo: 'Number of credits used for the request.',
    label: 'USED CREDITS'
  },
  {
    id: 'requestType',
    align: 'center',
    disablePadding: true,
    tooltip: true,
    tooltipInfo: 'It is a type of request, either Bulk Lookup or Add to Campaign.',
    label: 'REQUEST TYPE'
  },
  {
    id: 'status',
    align: 'center',
    disablePadding: true,
    label: 'STATUS'
  },

  {
    id: 'createdAt',
    align: 'center',
    disablePadding: false,
    label: 'CREATED AT'
  },
  {
    id: 'updatedAt',
    align: 'center',
    disablePadding: true,
    label: 'UPDATED AT'
  },
  {
    id: 'action',
    align: 'center',
    disablePadding: true,
    label: 'ACTION'
  }
];

function OrderTableHead({ order, orderBy, onRequestSort, id }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          if (id && headCell.id === 'action') {
            return null;
          }
          return (
            <TableCell
              key={headCell.id}
              align={headCell.align}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
              sx={{
                backgroundColor: 'rgb(242, 244, 246)',
                color: 'rgb(40, 40, 123)',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              {headCell.id !== 'status' ? (
                <>
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {headCell.label}
                    {headCell.tooltip && (
                      <Tooltip title={headCell.tooltipInfo} placement="right">
                        <InfoOutlinedIcon sx={{ width: '20px', marginLeft: '2px' }} />
                      </Tooltip>
                    )}
                    {orderBy === headCell.id && (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    )}
                  </TableSortLabel>
                </>
              ) : (
                headCell.label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

OrderTableHead.propTypes = {
  order: PropTypes.string,
  orderBy: PropTypes.string
};

export default function MoniterTable() {
  const navigate = useNavigate();
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [totalUser, setTotaluser] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [refetchUser, setRefetchUser] = useState();
  const [dateRange, setDateRange] = useState({});
  const { id } = useParams();
  const { searchData } = useSelector((state) => state.search);
  const [userId, setUserId] = useState(null);

  const {
    data,
    refetch,
    isLoading: refetchLoader
  } = useGetAllRequestsQuery({
    sortBy: orderBy,
    page: page + 1,
    order,
    ...(userId && { id: userId }),
    ...(dateRange?.start && { start: dateRange?.start }),
    ...(dateRange?.end && { end: dateRange?.end }),
    ...(searchData?.length && { search: searchData })
  });
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    setPage(0);
    refetch();
  }, [orderBy, order, userId, dateRange, searchData]);

  const handleUserDataView = (id) => {
    setUserId(id);
    let encoded = window.btoa(id);
    navigate(`/manage/monitor/${encoded}`);
  };

  const handleChangePage = (_event, newPage) => {
    setIsLoading(true);
    setPage(newPage);
  };

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if (refetchUser) {
      setIsLoading(true);
      setPage(0);
      refetch();
      setRefetchUser(false);
    }
  }, [refetchUser]);

  useEffect(() => {
    if (!refetchLoader) {
      setIsLoading(false);
    }
  }, [refetchLoader]);

  useEffect(() => {
    if (data?.data?.length) {
      const rows = data?.data?.map((cur) => {
        return createData(
          cur?.user?.email || '-',
          cur?.user?.name || '-',
          cur?.responseTime || 0,
          cur?.requestType || '-',
          cur?.leadsCount,
          cur?.usedCredits,
          cur?.status,
          cur?.createdAt,
          cur?.updatedAt,
          cur?._id,
          cur?.user?._id || '-'
        );
      });
      setIsLoading(false);
      setTotaluser(Number(data?.total) || 0);
      setRows(rows);
    } else {
      setRows([]);
      setTotaluser(0);
      setIsLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (id) {
      setUserId(window.atob(id));
    } else {
      setUserId(null);
    }
  }, [id]);

  return (
    <>
      {isLoading && refetchLoader && <LoaderCircle />}
      <>
        <Box sx={{ display: 'flex' }}>
          <Typography
            sx={{
              color: '#28287B',
              fontSize: '30px',
              fontWeight: 600,
              lineHeight: '28px',
              letterSpacing: '0px',
              margin: '5px 0px 20px 0px'
            }}
          >
            Monitor Lead Finder APIs
          </Typography>
          <Tooltip title="This will track bulk lookups and Add to campaign APIs with respect to users" placement="right">
            <InfoOutlinedIcon
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                color: '#28287B'
              }}
            />
          </Tooltip>
        </Box>
        <MonitorChart userId={userId} setDateRange={setDateRange} />
        <Box sx={{ width: { xs: '97%', sm: '30%' }, margin: '10px' }}>
          <Search />
        </Box>
        <Box
          sx={{
            border: '1px solid #ebebeb',
            borderRadius: '12px'
          }}
        >
          <TableContainer
            sx={{
              width: '100%',
              // height: 'calc(100vh - 200px)',
              overflowx: 'auto',
              maxHeight: '100vh', // 80vh
              transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              boxShadow: 'rgb(234, 236, 240) 0px 0px 1px, rgba(29, 41, 57, 0.08) 0px 1px 2px',
              borderRadius: '6px',
              color: 'rgb(16, 24, 40)',
              '& td, & th': { whiteSpace: 'nowrap' }
            }}
          >
            <GlobalStyles
              styles={{
                '*::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px'
                },
                '*::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgb(228, 228, 229)',
                  borderRadius: '10px',
                  border: '1px solid rgba(0, 0, 0, 0)'
                },
                '*::-webkit-scrollbar-track': {
                  borderRadius: '60px',
                  width: '4px',
                  backgroundColor: 'rgb(242, 244, 246)'
                }
              }}
            />
            <Table
              aria-labelledby="tableTitle"
              sx={{
                '& .MuiTableCell-root:first-of-type': {
                  pl: 2
                },
                '& .MuiTableCell-root:last-of-type': {
                  pr: 3
                }
              }}
              stickyHeader
            >
              <OrderTableHead id={id} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
              <TableBody>
                {stableSort(rows, getComparator(order, orderBy)).map((row) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      sx={{
                        '&:last-child td, &:last-child th': {
                          border: 0
                        }
                      }}
                      tabIndex={-1}
                      key={row._id}
                    >
                      <TableCell align="center">{row.email}</TableCell>
                      <TableCell align="center">{`${row.name?.first} ${row.name?.last}`}</TableCell>
                      <TableCell align="center">{row.responseTime ? Math.ceil(row.responseTime) : '-'}</TableCell>
                      <TableCell align="center">{row.leadsCount}</TableCell>
                      <TableCell align="center">{row.usedCredits}</TableCell>
                      <TableCell align="center">{row.requestType}</TableCell>
                      <TableCell align="center">{row.status}</TableCell>
                      <TableCell align="center">{getFormaDate(row.createdAt)}</TableCell>
                      <TableCell align="center">{getFormaDate(row.updatedAt)}</TableCell>
                      {!id && (
                        <TableCell align="center">
                          {
                            <Button
                              type="button"
                              className="py-3 px-4 flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-white b hover:bg-blue-700"
                              sx={{
                                padding: '8px 25px',
                                border: 'none',
                                backgroundColor: '#1890ff',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#0b7be3'
                                }
                              }}
                              onClick={() => {
                                handleUserDataView(row.userId);
                              }}
                            >
                              View
                            </Button>
                          }
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            sx={{
              border: '1px solid #f0f0f0',
              borderRadius: 2
            }}
            component="div"
            rowsPerPageOptions={[15]}
            rowsPerPage={15}
            count={totalUser}
            page={page}
            onPageChange={handleChangePage}
          />
        </Box>
      </>
    </>
  );
}
